import { FC, useState } from "react";
import { SpendCategory } from "../../../types/VendorTypes";
import { setProjectCategory } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { CategoryPicker } from "../../../common/components/CategoryPicker";
import { CategoryPath } from "../../../common/components/CategoryPath";
import { EditableField } from "../../../common/components/EditableField";

interface ProjectCategoryWidgetProps {
    projectId: number;
    categoryPath?: string[];
    /** The org's taxonomy (from useOrgCategories). */
    categories: SpendCategory[];
    canEdit: boolean;
    /** Called after the category was saved. */
    onUpdate: () => void;
}

/**
 * The project's category (branch above, name below). When the user can edit,
 * hovering reveals an edit affordance and clicking opens the category
 * picker. Read-only otherwise.
 */
export const ProjectCategoryWidget: FC<ProjectCategoryWidgetProps> = ({
    projectId,
    categoryPath,
    categories,
    canEdit,
    onUpdate,
}) => {
    const [open, setOpen] = useState(false);

    const trigger = (
        <EditableField canEdit={canEdit} fill>
            <CategoryPath path={categoryPath} />
        </EditableField>
    );

    if (!canEdit) return trigger;

    const save = async (path: string[] | null) => {
        setOpen(false);
        try {
            await setProjectCategory(projectId, path);
            onUpdate();
        } catch (error) {
            handleError(error);
        }
    };

    return (
        <CategoryPicker
            categories={categories}
            value={categoryPath}
            onSelect={save}
            onClear={() => save(null)}
            clearLabel="Clear category"
            open={open}
            onOpenChange={setOpen}
            trigger={trigger}
        />
    );
};
